import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { warmUpBackend } from '../services/api';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [warming, setWarming] = useState(true);
  const { login, loginWithMicrosoft, ssoError, loading } = useAuth();
  const navigate = useNavigate();
  const ssoConfigured = Boolean(process.env.REACT_APP_AZURE_CLIENT_ID && process.env.REACT_APP_AZURE_TENANT_ID);

  // Surface any error from a completed Microsoft redirect attempt
  useEffect(() => { if (ssoError) setError(ssoError); }, [ssoError]);

  const handleMicrosoftLogin = async () => {
    setError('');
    const r = await loginWithMicrosoft(); // full-page redirect — only returns here on failure to start
    if (!r.success && r.error) setError(r.error);
  };

  useEffect(() => {
    warmUpBackend().then(() => setWarming(false)).catch(() => setWarming(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    let r = await login(email, password);
    if (!r.success && (r.error?.includes('Network') || r.error?.includes('CORS') || r.error?.includes('ERR_'))) {
      setError('Server is waking up... retrying in a moment');
      await warmUpBackend();
      await new Promise(resolve => setTimeout(resolve, 2000));
      r = await login(email, password);
    }
    if (r.success) navigate('/');
    else setError(r.error);
  };

  const qk = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen flex" style={{ background: 'linear-gradient(135deg, #0c1b2e 0%, #162d4a 40%, #1e293b 100%)' }}>
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-12 h-12 rounded-xl bg-[#c8102e] flex items-center justify-center shadow-lg"><span className="text-white font-bold text-xl">S</span></div>
            <div><h1 className="text-white font-bold text-2xl">SiloGate</h1><p className="text-slate-400 text-xs uppercase tracking-[0.3em]">by TGLOR</p></div>
          </div>
          <h2 className="text-white font-bold text-4xl leading-tight mb-6">Institutional Planning,<br/>Performance & Risk<br/><span className="text-[#c8102e]">Management Platform</span></h2>
          <p className="text-slate-300 text-lg max-w-md leading-relaxed">Integrated strategic planning, project portfolio management, and enterprise risk management for Mintek.</p>
        </div>
        <p className="text-slate-500 text-sm relative z-10">Built for Mintek · Powered by TGLOR (Pty) Ltd</p>
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#c8102e]/5 rounded-full blur-3xl" />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="font-bold text-xl text-slate-800 mb-1">Sign In</h3>
            <p className="text-slate-500 text-sm mb-6">Access the SiloGate management platform</p>
            {warming && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-4 text-sm text-blue-700 flex items-center gap-2"><div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"/>Connecting to server...</div>}
            {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e]" placeholder="user@mintek.co.za" /></div>
              <div><label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#c8102e]/20 focus:border-[#c8102e]" /></div>
              <button type="submit" disabled={loading || warming} className="w-full py-2.5 bg-[#c8102e] text-white rounded-xl font-semibold text-sm hover:bg-[#a00d24] transition-colors disabled:opacity-50 shadow-lg">{loading ? 'Signing in...' : warming ? 'Connecting...' : 'Sign In'}</button>
            </form>
            {ssoConfigured && (
              <>
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                <button type="button" onClick={handleMicrosoftLogin} disabled={loading || warming}
                  className="w-full py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl font-semibold text-sm hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50 shadow-sm flex items-center justify-center gap-3">
                  <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none" aria-hidden="true">
                    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                  </svg>
                  Sign in with Microsoft
                </button>
                <p className="text-[10px] text-slate-400 text-center mt-2">Single Sign-On via Microsoft Entra ID (Active Directory)</p>
              </>
            )}
            <div className="mt-6 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-3 font-semibold">Demo Accounts <span className="text-[10px] normal-case tracking-normal text-slate-300">(click to fill)</span></p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {l:'🔧 Admin', e:'admin@silogate.co.za', p:'SiloGate@2026!', d:'Full access'},
                  {l:'👔 CEO / EXCO', e:'ceo@mintek.co.za', p:'Demo@2026!', d:'Final approver'},
                  {l:'📊 Group Exec (R&D)', e:'ge.rd@mintek.co.za', p:'Demo@2026!', d:'Cluster reviewer'},
                  {l:'📊 Group Exec (Corp)', e:'ge.corp@mintek.co.za', p:'Demo@2026!', d:'Cluster reviewer'},
                  {l:'🏢 EM Hydro (Div Head)', e:'em.hms@mintek.co.za', p:'Demo@2026!', d:'Initiates BP'},
                  {l:'🏢 EM Adv Materials', e:'em.amd@mintek.co.za', p:'Demo@2026!', d:'Initiates BP'},
                  {l:'🏢 EM Finance', e:'em.fin@mintek.co.za', p:'Demo@2026!', d:'Initiates BP'},
                  {l:'👤 Head SHEQ', e:'head.sheq@mintek.co.za', p:'Demo@2026!', d:'Edits draft BP'},
                ].map(d => (
                  <button key={d.e} onClick={() => qk(d.e, d.p)} className="text-xs px-3 py-2 bg-slate-50 rounded-lg hover:bg-slate-100 text-slate-600 text-left transition-colors">
                    <span className="font-semibold">{d.l}</span>
                    <span className="block text-[10px] text-slate-400 truncate">{d.e}</span>
                    <span className="block text-[9px] text-slate-300">{d.d}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
